/**
 * Patient-scoped notes API service for the V5 UI.
 * Uses the v5 api() client which sends JSON bodies and the X-Target-Company header.
 */
import { api, authHeaders, unwrapApiData } from "@/v5/lib/api";
import { downloadNoteAttachment } from "@/services/labs.service";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PatientNote {
  noteId: number;
  patientId: number;
  typeId: number;
  typeName: string;
  title: string;
  content: string;
  comments: string;
  noteDate: string | null;
  date: string | null;
  authorName: string;
  authorId: number;
  active: number;
  visibleToPatient: boolean;
  attachmentCount: number;
  /** Who soft-deleted it. Null when unknown -- deletions were not recorded before Sep 2026. */
  deletedBy?: string | null;
  /** When it was deleted. Null whenever deletedBy is. */
  deletedAt?: string | null;
}

export interface PatientNoteDetail extends PatientNote {
  attachments: NoteAttachment[];
  portalId: number | null;
  /**
   * Appointments this note is linked to. Optional because the key is absent on
   * older API builds, and the server returns [] when the `scheduling` module is
   * disabled for the account.
   */
  appointments?: NoteAppointmentLink[];
}

export interface NoteAttachment {
  attachmentId: number;
  filename: string;
  dateCreated: string | null;
}

/**
 * An appointment a note is linked to. The link is stored server-side against the
 * appointment's stable `auditRecordID`; `appointmentId` is the CURRENT live PK and
 * is what to send back to /appointments/{id}/... endpoints.
 */
export interface NoteAppointmentLink {
  linkId: number;
  appointmentId: number;
  auditRecordId: string;
  appointmentName: string;
  startTime: string | null; // Pacific "YYYY-MM-DD HH:MM:SS"
  endTime: string | null;
  appointmentTypeName: string;
  facilityName: string;
  cancelled: boolean;
  noShow: boolean;
  linkedDate: string | null;
  /** The appointment's patient was changed after this link was made. */
  patientMismatch: boolean;
}

export interface NoteHistoryEntry {
  historyId: number;
  title: string;
  content: string;
  comments: string;
  noteDate: string | null;
  typeId: number;
  typeName: string;
  authorName: string;
  dateCreated: string | null;
  attachmentsAdded: { attachmentId: number; filename: string }[];
  attachmentsRemoved: { attachmentId: number; filename: string }[];
}

export interface NoteTemplate {
  templateId: number;
  categoryId: number | null;
  categoryName: string | null;
  title: string;
  // listTemplates omits content/noteType/active to keep the response under
  // AWS Lambda's 6MB payload ceiling. Use renderTemplate to fetch content
  // when the user actually picks a template to apply.
  content?: string;
  noteType?: string;
  active?: boolean;
  userId: number;
  isShared: boolean;
}

export interface RenderedTemplate {
  templateId: number;
  title: string;
  content: string;
}

export interface NoteCategory {
  noteTypeID: number;
  name: string;
  active: boolean;
  isLab: number;
}

export interface NoteTask {
  taskId: number;
  name: string;
  active: number;
  priority: string;
  dateStarted: string | null;
  dateCompleted: string | null;
  assignedToId: number;
  noteId: number;
}

export interface EFormPlan {
  planId: number;
  name: string;
  date: string | null;
  patientId: number;
  displayId: string;
  lastContact: string | null;
  stageName: string;
  quoteCost: number | null;
  procedures: string[];
  procName: string;
  procQuantity: number;
  planTypeName: string;
}

export interface CreateNotePayload {
  title: string;
  content?: string;
  comments?: string;
  typeId?: number;
  noteDate?: string;
  noteTime?: string;
  notifyDoctor?: number;
  visibleToPatient?: boolean;
  taskIds?: number[];
  fileNames?: string;
  /**
   * Link the created note to this appointment (its current live PK; the server
   * resolves it to the stable auditRecordID and writes the link in the same
   * transaction).
   */
  appointmentId?: number;
}

/**
 * NOTE: deliberately has no `appointmentId`. Appointment links are owned by the
 * appointment side — created via CreateNotePayload.appointmentId, and changed via
 * POST/DELETE /appointments/{id}/notes. Keeping it off the update payload means
 * editing a note's body can never resurrect a link the user just removed, and an
 * edit launched from the patient Notes tab (which has no appointment context) can
 * never drop an existing one.
 */
export interface UpdateNotePayload {
  title?: string;
  content?: string;
  comments?: string;
  typeId?: number;
  noteDate?: string;
  noteTime?: string;
  notifyDoctor?: number;
  fileNames?: string;
  removeFiles?: number[];
}

export interface SaveAsTemplatePayload {
  title: string;
  content?: string;
  subject?: string;
  noteTypeId: number;
  isShared?: boolean;
}

// ─── Notes CRUD ───────────────────────────────────────────────────────────────

export async function listNotes(patientId: string | number, includeDeleted = false): Promise<PatientNote[]> {
  const params: Record<string, string> = {};
  if (includeDeleted) params.includeDeleted = "1";
  console.log(`[notes] listNotes patientId=${patientId} includeDeleted=${includeDeleted}`);
  try {
    const res = await api<{ data: PatientNote[] }>(`/patients/${patientId}/notes`, { params });
    const notes = unwrapApiData(res) as PatientNote[];
    console.log(`[notes] listNotes OK — ${notes.length} notes returned`);
    return notes;
  } catch (err: any) {
    console.error(`[notes] listNotes FAILED`, {
      status: err?.status ?? err?.response?.status,
      message: err?.message,
      body: err?.response?.data ?? err?.data,
      err,
    });
    throw err;
  }
}

export async function getNote(patientId: string | number, noteId: number): Promise<PatientNoteDetail> {
  console.log(`[notes] getNote patientId=${patientId} noteId=${noteId}`);
  try {
    const res = await api<{ data: PatientNoteDetail }>(`/patients/${patientId}/notes/${noteId}`);
    const note = unwrapApiData(res) as PatientNoteDetail;
    console.log(`[notes] getNote OK — noteId=${note.noteId} title="${note.title}"`);
    return note;
  } catch (err: any) {
    console.error(`[notes] getNote FAILED`, {
      status: err?.status ?? err?.response?.status,
      message: err?.message,
      err,
    });
    throw err;
  }
}

export async function createNote(patientId: string | number, payload: CreateNotePayload): Promise<PatientNoteDetail> {
  const res = await api<{ data: PatientNoteDetail }>(`/patients/${patientId}/notes`, { method: "POST", body: payload as unknown as Record<string, unknown> });
  return unwrapApiData(res) as PatientNoteDetail;
}

export async function updateNote(patientId: string | number, noteId: number, payload: UpdateNotePayload): Promise<PatientNoteDetail> {
  const res = await api<{ data: PatientNoteDetail }>(`/patients/${patientId}/notes/${noteId}`, { method: "PUT", body: payload as unknown as Record<string, unknown> });
  return unwrapApiData(res) as PatientNoteDetail;
}

export async function deleteNote(patientId: string | number, noteId: number | string): Promise<void> {
  await api(`/patients/${patientId}/notes/${noteId}`, { method: "DELETE" });
}

export async function restoreNote(patientId: string | number, noteId: number | string): Promise<void> {
  await api(`/patients/${patientId}/notes/${noteId}/restore`, { method: "POST" });
}

// ─── Portal visibility ───────────────────────────────────────────────────────

export async function togglePortalVisibility(patientId: string | number, noteId: number): Promise<{ visible: boolean; portalId: number | null }> {
  const res = await api<{ data: { visible: boolean; portalId: number | null } }>(`/patients/${patientId}/notes/${noteId}/portal`, { method: "POST" });
  return unwrapApiData(res) as { visible: boolean; portalId: number | null };
}

// ─── History ──────────────────────────────────────────────────────────────────

export async function getNoteHistory(patientId: string | number, noteId: number): Promise<NoteHistoryEntry[]> {
  const res = await api<{ data: NoteHistoryEntry[] }>(`/patients/${patientId}/notes/${noteId}/history`);
  return unwrapApiData(res) as NoteHistoryEntry[];
}

// ─── Print ────────────────────────────────────────────────────────────────────

type PrintOptions = { attachments?: boolean; logo?: boolean; headinginfo?: boolean; documentDetails?: boolean };

async function fetchPrintBlob(
  patientId: string | number,
  noteIds: string,
  accept: string,
  options?: PrintOptions,
): Promise<string> {
  const params = new URLSearchParams();
  if (options?.attachments === false) params.set("attachments", "0");
  if (options?.logo === false) params.set("logo", "0");
  if (options?.headinginfo === false) params.set("headinginfo", "0");
  if (options?.documentDetails === false) params.set("documentDetails", "0");
  const qs = params.toString();
  const base = (import.meta as any).env?.VITE_API_URL ?? "/api/v5";

  const headers: Record<string, string> = { Accept: accept, ...authHeaders() };

  const url = `${base}/patients/${patientId}/notes/${noteIds}/print${qs ? `?${qs}` : ""}`;
  console.log(`[notes] fetchPrint ${accept} → ${url}`);
  const res = await fetch(url, { headers });
  if (!res.ok) {
    let body: string | undefined;
    try { body = await res.text(); } catch { /* ignore */ }
    console.error(`[notes] fetchPrint FAILED`, { status: res.status, statusText: res.statusText, body });
    throw new Error(`Print failed: ${res.status}`);
  }
  const blob = await res.blob();
  console.log(`[notes] fetchPrint OK — blob ${blob.size} bytes, type=${blob.type}`);
  return URL.createObjectURL(blob);
}

const CHUNK_SIZE = 20;

export async function fetchNotePrintHtml(
  patientId: string | number,
  noteIds: string,
  options?: PrintOptions,
): Promise<string> {
  const ids = noteIds.split(",").filter(Boolean);

  if (ids.length <= CHUNK_SIZE) {
    return fetchPrintBlob(patientId, noteIds, "text/html", options);
  }

  console.log(`[notes] chunked print: ${ids.length} notes in ${Math.ceil(ids.length / CHUNK_SIZE)} batches`);

  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHUNK_SIZE));
  }

  const htmlParts: string[] = [];
  let wrapperHead = "";
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    console.log(`[notes] chunk ${i + 1}/${chunks.length}: ${chunk.length} notes`);
    const blobUrl = await fetchPrintBlob(patientId, chunk.join(","), "text/html", options);
    const res = await fetch(blobUrl);
    const html = await res.text();
    URL.revokeObjectURL(blobUrl);

    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    const bodyContent = bodyMatch ? bodyMatch[1] : html;

    if (i === 0) {
      const headMatch = html.match(/^[\s\S]*<body[^>]*>/i);
      wrapperHead = headMatch ? headMatch[0] : "<!DOCTYPE html><html><head></head><body>";
    }

    if (i > 0 && bodyContent.trim()) {
      htmlParts.push('<hr class="divider"/>');
    }
    htmlParts.push(bodyContent);
  }

  const combined = `${wrapperHead}${htmlParts.join("\n")}</body></html>`;
  const blob = new Blob([combined], { type: "text/html" });
  console.log(`[notes] chunked print complete — ${blob.size} bytes total`);
  return URL.createObjectURL(blob);
}

export function fetchNotePrintPdf(patientId: string | number, noteIds: string, options?: PrintOptions): Promise<string> {
  return fetchPrintBlob(patientId, noteIds, "application/pdf", options);
}

// ─── Attachments ──────────────────────────────────────────────────────────────

export interface UploadAttachmentOptions {
  /**
   * When true and the upload is an image, the API also copies the saved
   * file into the patient's photos_{accountId}/{patientId}/ folder so it
   * appears in the Patient Files tab and is reachable by
   * PatientPhotoResolver, matching legacy patient_photo_save.php parity.
   * The server ignores this for non-image MIME types. Default false keeps
   * the regular file picker / lab upload behavior unchanged.
   */
  mirrorToPhotos?: boolean;
  /**
   * Pre-categorize the attachment (Imaging/Genetics upload). When set, the API
   * stamps notes_attachments.file_type and marks the row already-processed so
   * the legacy Bedrock classifier never overwrites the category. Omit for the
   * normal Labs upload flow (category is assigned by the classifier).
   */
  fileType?: 'IMAGING' | 'GENETICS';
}

/**
 * A failed attachment upload. `message` is written for the person filing the
 * document, so callers can show it as-is; `step` and `status` are for logs.
 */
export class AttachmentUploadError extends Error {
  readonly step: "presign" | "storage" | "attach";
  readonly status: number;

  constructor(step: AttachmentUploadError["step"], status: number, file: File) {
    super(
      status === 401
        ? "You have been signed out. Sign in again, then attach the file again."
        : `"${file.name}" could not be attached. Try again. If it keeps failing, contact Prospera support.`,
    );
    this.name = "AttachmentUploadError";
    this.step = step;
    this.status = status;
  }
}

export async function uploadAttachment(
  patientId: string | number,
  noteId: number,
  file: File,
  options: UploadAttachmentOptions = {},
): Promise<{ attachmentId: number; filename: string }> {
  const SIZE_THRESHOLD = 4 * 1024 * 1024;
  const base = (import.meta as any).env?.VITE_API_URL ?? "/api/v5";
  const headers = authHeaders();

  const fail = (step: AttachmentUploadError["step"], status: number): never => {
    console.error(`[notes] uploadAttachment ${step} failed`, { status, size: file.size, type: file.type });
    throw new AttachmentUploadError(step, status, file);
  };

  if (file.size > SIZE_THRESHOLD) {
    const presignRes = await fetch(`${base}/uploads/presign`, {
      method: "POST",
      headers: { ...headers, "Content-Type": "application/json" },
      body: JSON.stringify({ filename: file.name, contentType: file.type || "application/octet-stream", size: file.size }),
    });
    if (!presignRes.ok) fail("presign", presignRes.status);
    const { uploadUrl, s3Key } = await presignRes.json();

    const putRes = await fetch(uploadUrl, { method: "PUT", body: file, headers: { "Content-Type": file.type || "application/octet-stream" } });
    if (!putRes.ok) fail("storage", putRes.status);

    const formData = new FormData();
    formData.append("s3Key", s3Key);
    if (options.mirrorToPhotos) formData.append("mirrorToPhotos", "1");
    if (options.fileType) formData.append("file_type", options.fileType);
    const res = await fetch(`${base}/patients/${patientId}/notes/${noteId}/attachments`, { method: "POST", headers, body: formData });
    if (!res.ok) fail("attach", res.status);
    const json = await res.json();
    return (json.data ?? json) as { attachmentId: number; filename: string };
  }

  const formData = new FormData();
  formData.append("file", file);
  if (options.mirrorToPhotos) formData.append("mirrorToPhotos", "1");
  if (options.fileType) formData.append("file_type", options.fileType);

  const res = await fetch(`${base}/patients/${patientId}/notes/${noteId}/attachments`, { method: "POST", headers, body: formData });
  if (!res.ok) fail("attach", res.status);
  const json = await res.json();
  return (json.data ?? json) as { attachmentId: number; filename: string };
}

export async function deleteAttachment(patientId: string | number, noteId: number, attachmentId: number): Promise<void> {
  await api(`/patients/${patientId}/notes/${noteId}/attachments/${attachmentId}`, { method: "DELETE" });
}

// ─── Attachment download ─────────────────────────────────────────────────────
//
// Note attachments are `notes_attachments` rows, and the endpoint that serves
// them properly is `/patients/{id}/lab-attachments/{attachId}` — it honours
// ?inline=1 for in-browser preview and, above 4MB, returns
// `{redirect: <presigned s3 url>}` instead of a body, because API Gateway caps a
// Lambda response at 6MB after base64. (See PatientNotesController::downloadAttachment,
// which now delegates there too.) downloadNoteAttachment already handles the
// redirect case, so notes gets large-file support rather than a second copy of it.
// The inline preview path goes through LabPdfPreviewPanel, which resolves the
// same endpoint via fetchNoteAttachmentViewUrl.
//
// `noteId` is deliberately not a parameter: the endpoint scopes by
// noteAttachmentID + accountID, which is the tenant boundary.

/**
 * The response is a Blob, so apiClient's interceptor cannot parse the API's
 * `{error:{message}}` envelope out of it and never sets `userMessage` — the raw
 * axios message is just "Request failed with status code 404". Read the blob so
 * the toast names the actual cause ("No file associated with this attachment.").
 */
async function attachmentErrorMessage(err: unknown): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      const msg = json?.error?.message ?? json?.message;
      if (msg) return String(msg);
    } catch { /* not JSON — fall through */ }
  }
  const userMessage = (err as { userMessage?: string })?.userMessage;
  if (userMessage) return userMessage;
  return err instanceof Error ? err.message : "Failed to download attachment";
}

/** Force-download a note attachment to disk (for non-previewable types). */
export async function downloadAttachment(
  patientId: string | number,
  attachmentId: number,
  filename: string,
): Promise<void> {
  try {
    await downloadNoteAttachment(Number(patientId), attachmentId, filename);
  } catch (err) {
    throw new Error(await attachmentErrorMessage(err));
  }
}

// ─── Lab post-processing ─────────────────────────────────────────────────────

/**
 * Kicks off the legacy textract OCR + biomarker extraction chain for a
 * freshly uploaded lab note. Mirrors processUploadedLab() in
 * note-handling-pdf.js which POSTs to /textract_autorun.php and then
 * /note_attachment_process_autorun.php on the v4 host. The v5 endpoint
 * proxies that chain server-side via HMAC-signed call to v4 so the SPA
 * doesn't have to live in the legacy origin.
 *
 * Fire-and-forget: the v5 controller returns as soon as the v4 proxy
 * accepts the request — actual textract work churns asynchronously.
 */
export async function processLab(patientId: string | number, noteId: number): Promise<{ queued: boolean }> {
  const res = await api<{ data: { queued: boolean } }>(
    `/patients/${patientId}/notes/${noteId}/process-lab`,
    { method: "POST", body: {} },
  );
  return unwrapApiData(res) as { queued: boolean };
}

// ─── Audio ────────────────────────────────────────────────────────────────────

export async function uploadAudio(patientId: string | number, noteId: number, audioBase64: string): Promise<{ attachmentId: number; filename: string }> {
  const res = await api<{ data: { attachmentId: number; filename: string } }>(`/patients/${patientId}/notes/${noteId}/audio`, { method: "POST", body: { audio: audioBase64 } });
  return unwrapApiData(res) as { attachmentId: number; filename: string };
}

// ─── Tasks ────────────────────────────────────────────────────────────────────

export async function getNoteTasks(patientId: string | number, noteId: number): Promise<NoteTask[]> {
  const res = await api<{ data: NoteTask[] }>(`/patients/${patientId}/notes/${noteId}/tasks`);
  return unwrapApiData(res) as NoteTask[];
}

// ─── E-Forms ──────────────────────────────────────────────────────────────────

export async function getEFormPlans(patientId: string | number): Promise<EFormPlan[]> {
  const res = await api<{ data: EFormPlan[] }>(`/patients/${patientId}/notes/eforms`);
  return unwrapApiData(res) as EFormPlan[];
}

// ─── Note Categories ──────────────────────────────────────────────────────────

export async function listNoteCategories(): Promise<NoteCategory[]> {
  const res = await api<{ data: NoteCategory[] }>("/note-categories");
  return unwrapApiData(res) as NoteCategory[];
}

// ─── Templates ────────────────────────────────────────────────────────────────

export async function listTemplates(): Promise<NoteTemplate[]> {
  const res = await api<{ data: NoteTemplate[] }>("/notes/templates");
  return unwrapApiData(res) as NoteTemplate[];
}

export async function renderTemplate(templateId: number, patientId: string | number): Promise<RenderedTemplate> {
  const res = await api<{ data: RenderedTemplate }>(`/notes/templates/${templateId}/render`, { params: { patientId: String(patientId) } });
  return unwrapApiData(res) as RenderedTemplate;
}

export async function saveAsTemplate(payload: SaveAsTemplatePayload): Promise<{ templateId: number }> {
  const res = await api<{ data: { templateId: number } }>("/notes/save-as-template", { method: "POST", body: payload as unknown as Record<string, unknown> });
  return unwrapApiData(res) as { templateId: number };
}

// Soft-deletes (active=0) a personal ("Mine") template. The backend rejects
// attempts to delete shared/office templates or another user's private one.
export async function deleteTemplate(templateId: number): Promise<void> {
  await api(`/notes/templates/${templateId}`, { method: "DELETE" });
}

// ─── E-Forms link ─────────────────────────────────────────────────────────────

export interface EFormLink {
  shortUrl: string;
  fullUrl: string;
}

export async function createEFormLink(
  patientId: string | number,
  payload: {
    surveyId: number;
    planId?: number;
    customCodes?: Record<string, string>;
    /** When true, mints a note-embed link (patientID=NA, planID empty, &note=true&indexKey={patientId}) — see PatientEFormsController::link. */
    forNote?: boolean;
  }
): Promise<EFormLink> {
  const res = await api<{ data: EFormLink }>(`/patients/${patientId}/eforms/link`, {
    method: "POST",
    body: payload as unknown as Record<string, unknown>,
  });
  return unwrapApiData(res) as EFormLink;
}

/**
 * Substitute ${...} / {!...} keywords in form HTML using V4's keyword
 * replacement engine. Mirrors the parent-side `replaceKeywordsForEform`
 * (patient-details-test.js:13729) that fires after the note-embed iframe
 * posts its rendered HTML back: passes the real patient id and the plan id
 * captured from the dropdown at click time, then inserts the response at
 * the cursor in the editor.
 */
export async function replaceEFormKeywords(
  patientId: string | number,
  payload: { planId?: number; content: string }
): Promise<{ content: string }> {
  const res = await api<{ data: { content: string } }>(
    `/patients/${patientId}/eforms/replace-keywords`,
    { method: "POST", body: payload as unknown as Record<string, unknown> }
  );
  return unwrapApiData(res) as { content: string };
}

// ─── AI / Transcription helpers ──────────────────────────────────────────────

export interface TranscribeStreamCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: string | null;
  region: string;
}

export interface ClassifyTypeResult {
  noteTypeId: number | null;
  confidence: "high" | "medium" | "low";
  reason: string | null;
}

export interface SuggestTemplatesResult {
  suggestions: { templateId: number; score: number; reason: string | null }[];
}

export interface FormatOption {
  id: string;
  label: string;
  description: string;
  prompt: string;
}

export interface SuggestFormatsResult {
  options: FormatOption[];
}

export interface FormatTranscriptResult {
  html: string;
  summary: string;
}

export interface TranscriptEntry {
  speakerLabel: string;
  text: string;
  startMs?: number | null;
  endMs?: number | null;
}

export async function getTranscribeStreamCredentials(): Promise<TranscribeStreamCredentials> {
  const res = await api<{ data: TranscribeStreamCredentials }>(
    "/notes/transcribe-stream/credentials",
    { method: "POST", body: {} }
  );
  return unwrapApiData(res) as TranscribeStreamCredentials;
}

export async function classifyNoteType(
  transcript: string,
  noteTypes: { noteTypeId: number; name: string }[]
): Promise<ClassifyTypeResult> {
  const res = await api<{ data: ClassifyTypeResult }>("/notes/classify-type", {
    method: "POST",
    body: { transcript, noteTypes } as unknown as Record<string, unknown>,
  });
  return unwrapApiData(res) as ClassifyTypeResult;
}

export async function suggestTemplates(
  transcript: string,
  templates: { templateId: number; title: string; categoryName?: string | null }[]
): Promise<SuggestTemplatesResult> {
  const res = await api<{ data: SuggestTemplatesResult }>("/notes/suggest-templates", {
    method: "POST",
    body: { transcript, templates } as unknown as Record<string, unknown>,
  });
  return unwrapApiData(res) as SuggestTemplatesResult;
}

export async function suggestFormats(transcript: string): Promise<SuggestFormatsResult> {
  const res = await api<{ data: SuggestFormatsResult }>("/notes/suggest-formats", {
    method: "POST",
    body: { transcript } as unknown as Record<string, unknown>,
  });
  return unwrapApiData(res) as SuggestFormatsResult;
}

export async function formatTranscript(
  transcript: string,
  prompt: string
): Promise<FormatTranscriptResult> {
  const res = await api<{ data: FormatTranscriptResult }>("/notes/format-transcript", {
    method: "POST",
    body: { transcript, prompt } as unknown as Record<string, unknown>,
  });
  return unwrapApiData(res) as FormatTranscriptResult;
}

export async function saveTranscriptEntries(
  patientId: string | number,
  noteId: number,
  payload: { entries: TranscriptEntry[]; planId?: number; summary?: string }
): Promise<{ transcriptId: number; entryCount: number }> {
  const res = await api<{ data: { transcriptId: number; entryCount: number } }>(
    `/patients/${patientId}/notes/${noteId}/transcript`,
    { method: "POST", body: payload as unknown as Record<string, unknown> }
  );
  return unwrapApiData(res) as { transcriptId: number; entryCount: number };
}

// ─── Surveys (used by E-Forms picker) ────────────────────────────────────────

export interface SurveyOption {
  id: number;
  title: string;
  isActive?: boolean;
  surveyOrder?: number | null;
}

export async function listSurveysForEForms(): Promise<SurveyOption[]> {
  // Legacy note-load-eform-display.php filters on `active = 1` server-side;
  // mirror that with ?active=1 so the picker only shows active surveys
  // (the V5 surveys index returns all rows by default).
  const res = await api<{ data: SurveyOption[] }>("/surveys", {
    params: { perPage: "200", active: "1" },
  });
  const raw = unwrapApiData(res);
  return Array.isArray(raw) ? (raw as SurveyOption[]) : [];
}

/** SCRUM-1806: whose name is printed on this lab file, against the chart. */
export interface LabSubjectCheck {
  /** exact | partial -> file it. none -> ask. unknown -> could not read; file it. */
  verdict: "exact" | "partial" | "none" | "unknown";
  documentName: string | null;
  documentDob?: string | null;
  patientName: string;
  reason: string;
}

/**
 * Check a lab file against the patient BEFORE uploading it.
 *
 * Advisory by design: any failure here resolves to `unknown` so this check can
 * never stop a clinic filing a lab. Multipart, so it borrows uploadAttachment's
 * header plumbing rather than the JSON api() client.
 */
export async function verifyLabSubject(
  patientId: string | number,
  file: File,
): Promise<LabSubjectCheck> {
  const base = (import.meta as any).env?.VITE_API_URL ?? "/api/v5";
  const headers = authHeaders();

  const body = new FormData();
  body.append("file", file);

  const res = await fetch(`${base}/patients/${patientId}/labs/verify-subject`, {
    method: "POST",
    headers,
    body,
  });
  if (!res.ok) throw new Error(`Verify failed: ${res.status}`);
  const json = await res.json();
  return (json.data ?? json) as LabSubjectCheck;
}
